const IMAGE_DECODE_TIMEOUT_MS = 320;

export interface PreloadedImageInfo {
  naturalWidth: number;
  naturalHeight: number;
  ok: boolean;
}

const imagePreloadCache = new Map<string, Promise<PreloadedImageInfo>>();

function decodeImageUrl(url: string): Promise<PreloadedImageInfo> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const imageInfo = {
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        ok: true,
      };
      if (typeof img.decode === "function") {
        img.decode().then(() => resolve(imageInfo)).catch(() => resolve(imageInfo));
        return;
      }
      resolve(imageInfo);
    };
    img.onerror = () => resolve({ naturalWidth: 0, naturalHeight: 0, ok: false });
    img.src = url;
  });
}

export function preloadImageInfo(url: string | null | undefined): Promise<PreloadedImageInfo | null> {
  if (!url) return Promise.resolve(null);

  const cached = imagePreloadCache.get(url);
  if (cached) return cached;

  const preloadPromise = decodeImageUrl(url);
  imagePreloadCache.set(url, preloadPromise);
  return preloadPromise;
}

export function preloadImageUrl(url: string | null | undefined): Promise<void> {
  if (!url) return Promise.resolve();

  const decodePromise = preloadImageInfo(url);
  const timeoutPromise = new Promise<void>((resolve) => {
    window.setTimeout(resolve, IMAGE_DECODE_TIMEOUT_MS);
  });
  return Promise.race([decodePromise, timeoutPromise]).then(() => undefined);
}

export function preloadImageUrls(urls: Array<string | null | undefined>): Promise<void> {
  const uniqueUrls = Array.from(new Set(urls.filter(Boolean))) as string[];
  if (uniqueUrls.length === 0) return Promise.resolve();

  return Promise.all(uniqueUrls.map(preloadImageUrl)).then(() => undefined);
}
